import React from 'react';
import {
  FormControl, MenuItem, Select, Typography, useTheme,
} from '@mui/material';
import { nanoid } from 'nanoid';
import AlertDialog from '../../../components/AlertDialog';
import analytics from '../../../utils/analytics';

export type WorkerType = 'DEPENDENT_PUBLIC'|'DEPENDENT_PRIVATE'|'INDEPENDENT'|'UNEMPLOYED'
const workerTypeTrackLabel = {
  DEPENDENT_PUBLIC: 'dependiente publico',
  DEPENDENT_PRIVATE: 'dependiente privado',
  INDEPENDENT: 'independiente',
  UNEMPLOYED: 'desempleado',
};
type DependentStatusDialogProps = {
    action: (workerType: WorkerType) => Promise<void>,
    isTriggered: boolean,
}

export default function DependentStatusDialog(props: DependentStatusDialogProps) {
  const {
    action,
    isTriggered,
  } = props;
  const [workerType, setWorkerType] = React.useState<WorkerType|'UNEMPLOYED'|''>('');
  const handleSetWorkerType = (wType: WorkerType|'UNEMPLOYED'|'') => {
    if (wType !== '') {
      analytics.track('Click Dropdown', {
        initiator: 'usuario',
        workerType: workerTypeTrackLabel[wType],
      });
    }
    setWorkerType(wType);
  };
  const [error, setError] = React.useState(0);
  const theme = useTheme();
  const handleAction = async () => {
    setError(0);
    try {
      const result = await action(workerType as WorkerType);
      return result;
    } catch (e) {
      return setError(e as number);
    }
  };
  const oncloseAction = () => {
    setError(0);
    setWorkerType('');
  };

  const errorContent = (errorCode: number) => {
    switch (errorCode) {
      case 400:
      case 422:
        return (
          <div key={errorCode}>
            <div>
              <span>
                Pareciera faltar información sobre tus boletas de honorarios.
              </span>
              {' '}
              <span>
                Por favor ingresa tu ClaveÚnica de nuevo luego de CERRAR esta ventana.
              </span>
            </div>
            <br />
            <div>
              Si el problema persiste, ¡Por favor, contáctanos!
            </div>
          </div>
        );
      case 423:
        return (
          <div key={errorCode}>
            <div>
              <span>
                Pareciera que no posees boletas de honorarios registradas.
              </span>
              {' '}
              <span>
                Por favor ingresa tu ClaveÚnica de nuevo luego de CERRAR esta ventana.
              </span>
              {' '}
              <div>
                Luego, escoge tu situación laboral nuevamente.
              </div>
            </div>
            <br />
            <div>
              Si el problema persiste, ¡Por favor, contáctanos!
            </div>
          </div>
        );
      case 509:
        return (
          <div key={errorCode}>
            <div>
              <span>
                No hemos podido procesar tu evaluación debido a que uno de nuestros
                {' '}
                proveedores de información se encuentra temporalmente fuera de servicio.
              </span>
              {' '}
              Por favor vuelve más tarde para que podamos
              {' '}
              <b style={{ color: theme.palette.secondary.main }}>
                evaluarte oportunamente.
              </b>
            </div>
            <br />
            <div>
              ¡Disculpa las molestias!
            </div>
          </div>
        );
      default:
        return (
          <span>
            Ha ocurrido un error. Por favor contáctanos.
          </span>
        );
    }
  };

  return (
    <AlertDialog
      onCloseAction={oncloseAction}
      dialogTitle={<div style={{ textAlign: 'center' }}><b>Información adicional</b></div>}
      dialogContent={(
        <div>
          <Typography gutterBottom>
            Por favor ayúdanos verificando tu situación laboral
            {' '}
            <span style={{ color: theme.palette.secondary.main }}>
              para continuar con tu evaluación.

            </span>
          </Typography>
          <Typography fontWeight="bold" gutterBottom>
            ¿Podrías indicarnos tu situación laboral?
          </Typography>
          <FormControl
            fullWidth
          >
            <Select
              value={workerType}
              MenuProps={{ keepMounted: true, disablePortal: true }}
              variant="outlined"
              onChange={(e) => handleSetWorkerType(e.target.value as WorkerType|'')}
              displayEmpty
              renderValue={
                workerType !== '' ? undefined : () => <Typography color="text.secondary">Seleccionar...</Typography>
                        }
            >
              <MenuItem
                value="INDEPENDENT"
                style={{ whiteSpace: 'normal' }}
              >
                <span key={nanoid()}>
                  Soy trabajador
                  {' '}
                  <b>INDEPENDIENTE</b>
                  {' '}
                  (Utilizo boletas de honorarios como fuente principal de ingresos)
                </span>
              </MenuItem>
              <MenuItem
                value="DEPENDENT_PUBLIC"
                style={{ whiteSpace: 'normal' }}
              >
                <span>
                  Trabajo para una empresa o institución
                  {' '}
                  <b>PÚBLICA</b>
                  {' '}
                  (Municipalidad, ministerio, hospital, carabineros...)
                </span>
              </MenuItem>
              <MenuItem
                value="DEPENDENT_PRIVATE"
                style={{ whiteSpace: 'normal' }}
              >
                <span key={nanoid()}>
                  Trabajo para una empresa o institución
                  {' '}
                  <b>PRIVADA</b>
                  {' '}
                  (Tengo un contrato vigente y recibo un salario)
                </span>
              </MenuItem>
              <MenuItem
                id="UNEMPLOYED"
                key="UNEMPLOYED"
                value="UNEMPLOYED"
                style={{ whiteSpace: 'normal' }}
              >
                <span key={nanoid()}>
                  Estoy
                  {' '}
                  <b>
                    desempleado
                  </b>
                  {' '}
                  o
                  no recibo un salario de manera formal
                </span>
              </MenuItem>
            </Select>
          </FormControl>
          {!!error
          && (
          <Typography
            color="error"
            variant="body2"
            marginTop={1}
            component="div"
          >
            {errorContent(error)}
          </Typography>
          )}
        </div>
  )}
      isTriggered={isTriggered}
      action={handleAction}
      button={<div />}
      hideCloseButton={!(!!error && [400, 423, 422, 509].includes(error))}
      submitButtonLabel="CONFIRMAR"
      submitButtonDisabled={!workerType || !!error}
    />
  );
}
